import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { Button, QrCodeImage, LoadableImage } from '@renderer/components'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import PinModal from '@renderer/components/PinModal'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import MidtransUtils, { VoucherOnlyRegionError } from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useDesignLayout } from '@renderer/hooks'
import { getInternetStatus, onInternetStatusChanged } from '@renderer/lib/runtime'
import {
  closeToyyibpayBill,
  hasToyyibpayBillApi,
  openToyyibpayBill,
} from '@renderer/lib/runtime/bridge'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import type { Package } from '@renderer/types/configState'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

export interface AiEffectPaidInfo {
  amount: number
  label: string
  via: 'gateway' | 'voucher' | 'bypass'
}

interface PostSessionAiPaymentModalProps {
  isVisible: boolean
  packages: Package[]
  themePrimary: string
  /** Editable intro title/description (dashboard-configured), shown above the price. */
  title?: string
  description?: string
  /** The customer's pre-AI photo shown in the preview pane (what they're paying to transform). */
  beforeUrl?: string
  /** Idle auto-skip timeout (seconds) for the selection screen. Defaults to 60. */
  timeout?: number
  onPaid: (info: AiEffectPaidInfo) => void
  onSkip: () => void
}

/**
 * Customer-facing payment for the AI effect (real money). This is a deliberate clone of
 * `UserReprintModal` — same full-screen two-pane shell, countdown, QR / Snap / redirect-bill-window
 * rails, expiry handling, and cancel behavior — adapted from "extra prints" to "AI effect packages"
 * (no quantity stepper; the left pane previews the AI result instead of the strip). On settlement
 * (or operator PIN bypass / cash voucher) it calls `onPaid`; the parent (PostSessionAI) then writes
 * the session transaction fields, uploads the session, and runs the AI generation — the same
 * session-update + server-side wallet settlement path the reprint flow uses.
 */
const PostSessionAiPaymentModal: React.FC<PostSessionAiPaymentModalProps> = ({
  isVisible,
  packages,
  themePrimary,
  title,
  description,
  beforeUrl,
  timeout = 60,
  onPaid,
  onSkip,
}) => {
  const { t } = useTranslation()
  const { isCompact: rpCompact, isTiny: rpTiny, isLandscape, isSpacious } = useDesignLayout()
  const rpModalRowLayout = isLandscape
  const rpLandscapeShort = rpCompact && isLandscape
  const rpContentJustify = rpModalRowLayout ? 'justify-center' : 'justify-start'

  // --- Container sizing (mirrors UserReprintModal) ---
  const outerPad = rpCompact ? 'p-2' : isSpacious ? 'p-4' : 'p-6'
  const glassPanelClass = rpCompact
    ? 'h-auto w-full max-w-7xl max-h-[96dvh] overflow-y-auto'
    : rpModalRowLayout
      ? `h-auto w-full max-h-[92dvh] overflow-y-auto ${isSpacious ? 'max-w-[min(94vw,1400px)]' : 'max-w-4xl'}`
      : `h-auto w-full max-h-[94dvh] overflow-y-auto ${isSpacious ? 'max-w-[min(92vw,900px)]' : 'max-w-[min(90vw,640px)]'}`

  const previewPaneClass = rpModalRowLayout
    ? rpLandscapeShort
      ? 'min-h-0 min-w-0 max-h-full w-[38%] max-w-[40%] shrink-0 p-2'
      : rpCompact
        ? 'min-h-0 min-w-0 max-h-full w-[38%] max-w-[40%] shrink-0 p-2'
        : isSpacious
          ? 'min-h-0 min-w-0 max-h-full w-[40%] max-w-[42%] shrink-0 p-6 sm:p-8'
          : 'min-h-0 min-w-0 max-h-full w-[340px] shrink-0 p-6'
    : rpCompact
      ? 'w-full shrink-0 p-2'
      : isSpacious
        ? 'w-full shrink-0 p-6 sm:p-8'
        : 'max-h-[min(40dvh,440px)] min-h-[10rem] w-full shrink-0 p-6'

  const previewMediaMax = rpModalRowLayout
    ? 'max-h-full max-w-full'
    : rpCompact
      ? 'max-h-[min(36dvh,300px)] max-w-full'
      : isSpacious
        ? 'max-h-[min(62dvh,820px)] w-auto max-w-[min(88vw,560px)]'
        : 'max-h-[min(40dvh,440px)] max-w-full'

  const contentPadClass = rpLandscapeShort
    ? 'px-3 py-2'
    : rpModalRowLayout && rpCompact
      ? 'px-4 py-3'
      : rpModalRowLayout
        ? isSpacious
          ? 'p-10 lg:p-12'
          : 'p-10'
        : rpCompact
          ? 'px-4 py-3'
          : isSpacious
            ? 'px-10 py-8 sm:px-12 sm:py-10'
            : 'px-8 py-6 sm:p-10'

  // --- Typography (mirrors UserReprintModal) ---
  const titleClass = rpTiny
    ? 'mb-1 text-lg font-bold text-gray-800'
    : rpCompact
      ? 'mb-2 text-xl font-bold text-gray-800'
      : isSpacious
        ? 'mb-4 text-5xl font-bold text-gray-800'
        : 'mb-4 text-4xl font-bold text-gray-800'
  const subtitleClass = rpTiny
    ? 'text-xs leading-snug text-gray-600'
    : rpCompact
      ? 'text-sm leading-relaxed text-gray-600'
      : isSpacious
        ? 'text-2xl leading-relaxed text-gray-600'
        : 'text-xl leading-relaxed text-gray-600'
  const sectionGap = rpTiny ? 'mb-3' : rpCompact ? 'mb-4' : isSpacious ? 'mb-10' : 'mb-8'
  const buttonGap = rpTiny ? 'gap-1.5' : rpCompact ? 'gap-2' : isSpacious ? 'gap-5' : 'gap-4'
  // Use the standard Button sizing (clamp-based) for consistency with the rest of the booth;
  // just stretch full-width within the content column.
  const primaryBtn = 'w-full'
  const secondaryBtn = 'w-full'
  const paymentTitleClass = rpTiny
    ? 'mb-2 text-lg font-bold'
    : rpCompact
      ? 'mb-3 text-xl font-bold'
      : isSpacious
        ? 'mb-6 text-4xl font-bold'
        : 'mb-6 text-3xl font-bold'
  const paymentQrBoxClass = rpTiny
    ? 'mb-2 flex h-[140px] w-[140px] flex-col items-center justify-center rounded-2xl bg-white p-3 shadow-lg'
    : rpCompact
      ? 'mb-3 flex h-[180px] w-[180px] flex-col items-center justify-center rounded-2xl bg-white p-4 shadow-lg'
      : isSpacious
        ? 'mb-6 flex h-[340px] w-[340px] flex-col items-center justify-center rounded-2xl bg-white p-8 shadow-lg'
        : 'mb-6 flex h-[300px] w-[300px] flex-col items-center justify-center rounded-2xl bg-white p-8 shadow-lg'
  const paymentPriceClass = rpTiny
    ? 'text-xl font-bold'
    : rpCompact
      ? 'text-2xl font-bold'
      : isSpacious
        ? 'text-4xl font-bold'
        : 'text-3xl font-bold'
  const countdownClass = rpTiny ? 'text-xs' : rpCompact ? 'text-sm' : isSpacious ? 'text-xl' : 'text-lg'
  const countdownPos = rpTiny ? 'right-2 top-2' : rpCompact ? 'right-3 top-3' : isSpacious ? 'right-8 top-8' : 'right-6 top-6'

  const { config: globalConfig } = useConfigStore()
  const { id, userPreferences, logActivity } = useSessionStateStore()
  const { password } = useSettingsStore()
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)

  const tier = globalConfig?.paymentSetupTier
  const hasCustomGateway = Boolean(globalConfig?.hasCustomGateway)
  // `global_hidden` (e.g. MXN) is voucher-only ONLY without a custom gateway;
  // a wired Mercado Pago makes online payment available. See view/Payment.tsx.
  const isVoucherOnlyRegion =
    (tier === 'global_hidden' || tier === 'localized_in_progress') &&
    !hasCustomGateway

  const activePackages = useMemo(
    () => packages.filter((pkg) => pkg.isActive !== false),
    [packages]
  )
  // AI effect is a single package (one paid enhancement per session) — always the first active one.
  const selectedPackage = activePackages[0]
  const currentPrice = selectedPackage?.price || 0
  const paymentTimeoutConfig = globalConfig?.pages?.payment?.settings?.paymentTimeout || 120

  const [isProcessing, setIsProcessing] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [showPinModal, setShowPinModal] = useState(false)
  const [showVoucherInput, setShowVoucherInput] = useState(false)
  const [voucherError, setVoucherError] = useState('')
  const [isVoucherLoading, setIsVoucherLoading] = useState(false)
  const [qrUrl, setQrUrl] = useState('')
  const [snapToken, setSnapToken] = useState('')
  const [redirectUrl, setRedirectUrl] = useState('')
  const [billWindowOpen, setBillWindowOpen] = useState(false)
  const [billWindowError, setBillWindowError] = useState<string | null>(null)
  const billWindowOpenedForUrlRef = useRef<string | null>(null)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [isOnline, setIsOnline] = useState(true)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [remainingTime, setRemainingTime] = useState<number>(timeout)

  const isMountedRef = useRef(true)
  const countdownRef = useRef<NodeJS.Timeout | null>(null)
  const paymentHandledRef = useRef(false)

  // --- Lifecycle: start countdown + internet tracking while visible; reset everything otherwise. ---
  useEffect(() => {
    if (!isVisible) {
      setShowPayment(false)
      setIsProcessing(false)
      setQrUrl('')
      setSnapToken('')
      setRedirectUrl('')
      setOrderId(null)
      setIsCheckingPayment(false)
      setIsExpired(false)
      setBillWindowError(null)
      setBillWindowOpen(false)
      billWindowOpenedForUrlRef.current = null
      paymentHandledRef.current = false
      return
    }

    isMountedRef.current = true
    setRemainingTime(timeout)
    setShowPayment(false)
    setIsProcessing(false)

    countdownRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (isMountedRef.current && !showPayment) {
            logActivity('AI effect payment modal timed out, skipping')
            onSkip()
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    void getInternetStatus().then((status) => setIsOnline(status))
    const unsubscribeNet = onInternetStatusChanged(setIsOnline)

    return (): void => {
      isMountedRef.current = false
      if (countdownRef.current) clearInterval(countdownRef.current)
      unsubscribeNet()
      closeToyyibpayBill()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, timeout])

  // Pause the idle countdown once the customer is on the payment screen.
  useEffect(() => {
    if (showPayment && countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [showPayment])

  const finishPaid = (via: AiEffectPaidInfo['via']): void => {
    if (paymentHandledRef.current) return
    paymentHandledRef.current = true
    setIsCheckingPayment(false)
    closeToyyibpayBill()
    onPaid({ amount: currentPrice, label: selectedPackage?.description || 'AI effect', via })
  }

  const handlePay = async (): Promise<void> => {
    if (!selectedPackage) {
      cogoToast.error(t('result.postSessionAi.errorGeneric', 'Something went wrong.'))
      return
    }
    if (isVoucherOnlyRegion) {
      logActivity('AI effect redirected to voucher entry (voucher-only region)', {
        paymentSetupTier: tier,
      })
      setVoucherError('')
      setShowVoucherInput(true)
      return
    }
    if (!isOnline) {
      cogoToast.error(t('payment.offline', 'No internet connection'))
      return
    }
    setIsProcessing(true)
    setShowPayment(true)
    setIsExpired(false)
    setQrUrl('')
    setSnapToken('')
    setRedirectUrl('')
    paymentHandledRef.current = false
    try {
      const data = await MidtransUtils.initiatePayment(
        selectedPackage.price,
        id || '',
        paymentTimeoutConfig,
        globalConfig?._id || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        0,
        false,
        'midtrans',
        true // isAiEffect
      )
      if (!data.order_id) throw new Error('Failed to generate payment order')
      setOrderId(data.order_id)
      if (data.qr_url) {
        setQrUrl(data.qr_url.replace(/([^:]\/)\/.+/g, '$1'))
        setIsCheckingPayment(true)
        logActivity('AI effect payment initiated (QR)', {
          orderId: data.order_id,
          amount: selectedPackage.price,
        })
      } else if (data.snap_token) {
        setSnapToken(data.snap_token)
        setIsCheckingPayment(true)
        logActivity('AI effect payment initiated (Snap)', { orderId: data.order_id })
      } else if (data.redirect_url) {
        // Redirect rail (e.g. ToyyibPay): poll for settlement while the frameless bill window
        // (auto-opened by the effect below) collects payment. Previously this threw "Invalid
        // payment response", so AI payment was broken in redirect-gateway regions.
        setRedirectUrl(data.redirect_url)
        setIsCheckingPayment(true)
        logActivity('AI effect payment initiated (Redirect)', {
          orderId: data.order_id,
          provider: data.provider,
        })
      } else {
        throw new Error('Invalid payment response')
      }
    } catch (error) {
      if (error instanceof VoucherOnlyRegionError) {
        setShowPayment(false)
        setIsProcessing(false)
        setVoucherError('')
        setShowVoucherInput(true)
        return
      }
      console.error('AI effect payment initiation error:', error)
      cogoToast.error(t('result.postSessionAi.errorGeneric', 'Something went wrong.'))
      setShowPayment(false)
      setIsProcessing(false)
    }
  }

  const openSnapPayment = (): void => {
    if (!snapToken || !window.snap) {
      cogoToast.error(t('payment.snapError', 'Payment gateway not ready'))
      return
    }
    window.snap.pay(snapToken, {
      onSuccess: () => finishPaid('gateway'),
      onPending: () => setIsCheckingPayment(true),
      onError: () => cogoToast.error(t('payment.snapError', 'Payment failed')),
      onClose: () => {},
    })
  }

  // Open the redirect gateway bill in a frameless kiosk window via the bridge (mirrors reprint /
  // the Payment page). Only opens once per URL; status polling settles regardless of when it closes.
  const openBillWindow = useCallback(
    async (urlOverride?: string): Promise<void> => {
      const url = urlOverride || redirectUrl
      if (!url) return
      if (!hasToyyibpayBillApi()) {
        setBillWindowError(t('payment.popupUnavailable', 'Payment window is unavailable in this build.'))
        return
      }
      try {
        setBillWindowError(null)
        setBillWindowOpen(true)
        billWindowOpenedForUrlRef.current = url
        const result = await openToyyibpayBill(url)
        if (result.status === 'reused') return
        setBillWindowOpen(false)
        if (result.status === 'unavailable') {
          setBillWindowError(t('payment.popupUnavailable', 'Payment window is unavailable in this build.'))
          logActivity('AI effect bill window unavailable', { url, reason: result.reason })
        } else if (result.status === 'failed') {
          setBillWindowError(t('payment.popupFailed', 'Could not open the payment window. Please try again.'))
          logActivity('AI effect bill window failed', { url, reason: result.reason })
        } else {
          logActivity('AI effect bill window closed', { url, finalUrl: result.finalUrl })
        }
      } catch (error) {
        setBillWindowOpen(false)
        setBillWindowError(t('payment.popupFailed', 'Could not open the payment window. Please try again.'))
        logActivity('AI effect bill window error', {
          url,
          error: error instanceof Error ? error.message : 'unknown',
        })
      }
    },
    [redirectUrl, t]
  )

  const handleCancelPayment = (): void => {
    setShowPayment(false)
    setIsCheckingPayment(false)
    setOrderId(null)
    setIsProcessing(false)
    setSnapToken('')
    setQrUrl('')
    setRedirectUrl('')
    setBillWindowError(null)
    setBillWindowOpen(false)
    billWindowOpenedForUrlRef.current = null
    closeToyyibpayBill()

    // Returning to the selection screen restarts the idle countdown (mirrors reprint).
    setRemainingTime(timeout)
    countdownRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (isMountedRef.current && !showPayment) {
            logActivity('AI effect payment modal timed out, skipping')
            onSkip()
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handlePinSubmit = (pin: string): void => {
    if (pin === password) {
      logActivity('AI effect payment bypassed with PIN')
      setShowPinModal(false)
      if (showPayment) handleCancelPayment()
      finishPaid('bypass')
    } else {
      cogoToast.error(t('payment.wrongCode', 'Incorrect code. Please try again.'))
    }
  }

  // Expose payment-active state to the chrome (ControlButtons) so an operator can PIN-bypass the
  // AI-effect charge, mirroring the reprint flow. Bypass settles as 'bypass' (no money) and then
  // generation proceeds.
  useEffect(() => {
    const dispatch = (visible: boolean): void => {
      window.dispatchEvent(new CustomEvent('ai-effect-payment-state', { detail: { visible } }))
    }
    dispatch(isVisible)
    return (): void => dispatch(false)
  }, [isVisible])

  useEffect(() => {
    const handleBypass = (): void => {
      logActivity('AI effect payment bypassed via Control Panel')
      if (showPayment) handleCancelPayment()
      finishPaid('bypass')
    }
    window.addEventListener('ai-effect-bypass', handleBypass)
    return (): void => window.removeEventListener('ai-effect-bypass', handleBypass)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPayment])

  const handleVoucherSubmit = async (code: string): Promise<void> => {
    if (!selectedPackage) return
    const trimmed = (code || '').trim()
    if (!trimmed) {
      setVoucherError(t('checkout.invalidPromo', 'Invalid promo code'))
      return
    }
    setVoucherError('')
    setIsVoucherLoading(true)
    try {
      const subtotal = Math.max(0, selectedPackage.price ?? 0)
      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: trimmed,
          projectId: globalConfig?._id || '',
          amount: Math.round(subtotal),
        }),
      })
      const data = await response.json().catch(() => ({}))
      const isValid = response.ok && data.isValid
      if (!isValid) {
        const msg = data.message || data.error || t('checkout.invalidPromo', 'Invalid promo code')
        setVoucherError(msg)
        cogoToast.error(msg)
        return
      }
      if (data.isCash) {
        logActivity('AI effect cash voucher applied', { code: trimmed, amount: subtotal })
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowVoucherInput(false)
        finishPaid('voucher')
        return
      }
      const apiDiscount = Math.round(Number(data.discount) || 0)
      const finalAmount = Math.max(0, Math.round(subtotal - apiDiscount))
      cogoToast.info(
        t(
          'reprint.voucherDiscountedSettleOffline',
          'Voucher applied. Please settle {{amount}} with the operator to continue.',
          { amount: formatAmount(finalAmount, projectCurrency) }
        )
      )
    } catch (error) {
      console.error('AI effect voucher validation error:', error)
      const msg = t('checkout.promoValidationFailed', 'Failed to validate promo code. Please try again.')
      setVoucherError(msg)
      cogoToast.error(msg)
    } finally {
      setIsVoucherLoading(false)
    }
  }

  // Auto-open the redirect bill popup once a URL is available (once per URL).
  useEffect(() => {
    if (!showPayment) return
    if (!redirectUrl) return
    if (isExpired) return
    if (paymentHandledRef.current) return
    if (billWindowOpenedForUrlRef.current === redirectUrl) return
    void openBillWindow(redirectUrl)
  }, [showPayment, redirectUrl, isExpired, openBillWindow])

  // Close the popup once the payment expires so the user isn't left on a dead bill page.
  useEffect(() => {
    if (isExpired) closeToyyibpayBill()
  }, [isExpired])

  useInterval(
    async () => {
      if (paymentHandledRef.current || !orderId) return
      try {
        const status = await MidtransUtils.checkPaymentStatus(orderId)
        if (status.isExpired) {
          setIsExpired(true)
          setIsCheckingPayment(false)
          cogoToast.warn(t('payment.expired', 'Payment expired'))
        } else if (status.paid) {
          cogoToast.success(t('payment.successMessage', 'Payment successful!'))
          logActivity('AI effect payment successful (verified)', { orderId })
          finishPaid('gateway')
        }
      } catch (error) {
        console.error('Error checking AI effect payment status:', error)
      }
    },
    isCheckingPayment && isOnline ? 3000 : null
  )

  if (!isVisible) return null

  return (
    <BackgroundContainer className={`fixed inset-0 z-50 flex items-center justify-center ${outerPad}`}>
      <GlassPanel className={`relative z-10 mx-auto flex flex-col rounded-3xl ${glassPanelClass}`}>
        {/* Idle countdown (selection screen only) */}
        {!showPayment && (
          <div className={`absolute z-10 ${countdownPos}`}>
            <span className={`font-mono font-semibold tabular-nums text-gray-700 ${countdownClass}`}>
              {Math.floor(remainingTime / 60)}:{String(remainingTime % 60).padStart(2, '0')}
            </span>
          </div>
        )}

        <div className={`flex w-full min-h-0 ${rpModalRowLayout ? 'min-h-0 flex-row' : 'flex-col'}`}>
          {/* Customer's before photo — what they're paying to transform */}
          <div
            className={`flex items-center justify-center bg-gray-50/50 ${rpModalRowLayout ? 'overflow-hidden' : 'overflow-visible'} ${previewPaneClass}`}
          >
            <div className="relative flex min-h-[10rem] w-full min-w-0 items-center justify-center">
              <LoadableImage
                src={beforeUrl}
                alt={t('result.postSessionAi.previewBefore', 'Before')}
                className={`h-auto w-auto rounded-lg object-contain object-center drop-shadow-2xl ${previewMediaMax}`}
              />
            </div>
          </div>

          {/* Content */}
          <div
            className={`custom-scrollbar flex min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden bg-white/40 ${rpModalRowLayout ? 'flex-1' : 'w-full shrink-0'} ${rpContentJustify} ${contentPadClass}`}
          >
            {showPayment ? (
              <div className={`flex animate-fade-in flex-col items-center justify-center ${rpTiny ? 'py-2' : 'py-4'}`}>
                <h2 className={`text-gray-800 ${paymentTitleClass}`}>
                  {t('reprint.scanToPay', 'Complete Payment')}
                </h2>

                <div className={paymentQrBoxClass}>
                  {isExpired ? (
                    <div className="flex flex-col items-center text-red-500">
                      <p className="mb-2 font-bold">{t('payment.expired', 'Payment expired')}</p>
                      <button onClick={() => void handlePay()} className="text-sm text-blue-600 underline">
                        {t('payment.retry', 'Try Again')}
                      </button>
                    </div>
                  ) : qrUrl ? (
                    <QrCodeImage
                      qrUrl={qrUrl}
                      className="h-full w-full object-contain"
                      onEvent={qrActivityLogger(logActivity, 'ai-effect-payment')}
                    />
                  ) : snapToken ? (
                    <div className={`flex flex-col items-center ${rpTiny ? 'gap-2' : 'gap-4'}`}>
                      <p className={`text-center text-gray-600 ${rpTiny ? 'text-xs' : rpCompact ? 'text-sm' : 'text-base'}`}>
                        {t('payment.snapReady', 'Secure payment ready')}
                      </p>
                      <Button text={t('payment.openSnap', 'Pay Now')} onClick={openSnapPayment} variant="primary" className={primaryBtn} />
                    </div>
                  ) : redirectUrl ? (
                    billWindowError ? (
                      <div className={`flex flex-col items-center text-center ${rpTiny ? 'gap-2' : 'gap-4'}`}>
                        <p className={`text-red-500 ${rpTiny ? 'text-xs' : 'text-sm'}`}>{billWindowError}</p>
                        <Button
                          text={t('payment.popupRetry', 'Retry')}
                          onClick={() => openBillWindow()}
                          disabled={billWindowOpen}
                          variant="primary"
                          className={primaryBtn}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <div className={`animate-spin rounded-full border-b-2 border-blue-600 ${rpTiny ? 'h-8 w-8' : 'h-12 w-12'}`} />
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center">
                      <div
                        className="h-10 w-10 animate-spin rounded-full border-2 border-t-transparent"
                        style={{ borderColor: themePrimary, borderTopColor: 'transparent' }}
                      />
                    </div>
                  )}
                </div>

                <p className={`mb-2 text-gray-800 ${paymentPriceClass}`}>
                  {formatAmount(currentPrice, projectCurrency)}
                </p>
                <p
                  className={`max-w-sm text-center text-gray-600 ${rpTiny ? 'mb-3 text-xs' : rpCompact ? 'mb-4 text-sm' : isSpacious ? 'mb-8 text-lg' : 'mb-8 text-base'}`}
                >
                  {qrUrl
                    ? t('reprint.paymentInstruction', 'Scan the QR code to complete purchase')
                    : redirectUrl
                      ? t('reprint.redirectInstruction', 'Complete payment in the window that just opened.')
                      : t('payment.snapOpening', 'Tap Pay Now to complete your purchase securely.')}
                </p>

                <Button
                  onClick={handleCancelPayment}
                  text={t('reprint.cancel', 'Cancel')}
                  variant="secondary"
                  className={`max-w-sm ${secondaryBtn}`}
                />
              </div>
            ) : (
              <>
                {/* Single AI effect package: title + description + price, then Pay → QRIS directly. */}
                <div className={`text-center ${sectionGap}`}>
                  <h1 className={titleClass}>{title || t('result.postSessionAi.title', 'AI enhancement')}</h1>
                  <p className={subtitleClass}>
                    {description || t('result.postSessionAi.subtitle', 'Optional polish for your photos.')}
                  </p>
                </div>

                <div className={`text-center ${sectionGap}`}>
                  <p className={`text-gray-900 ${paymentPriceClass}`}>
                    {formatAmount(currentPrice, projectCurrency)}
                  </p>
                  {selectedPackage?.description ? (
                    <p className={`mt-1 text-gray-600 ${rpCompact ? 'text-sm' : 'text-base'}`}>
                      {selectedPackage.description}
                    </p>
                  ) : null}
                </div>

                <div className={`flex flex-col items-center ${buttonGap}`}>
                  <Button
                    text={
                      isVoucherOnlyRegion
                        ? t('reprint.useVoucher', 'Use Voucher Code')
                        : t('reprint.print', 'Pay')
                    }
                    onClick={() => void handlePay()}
                    disabled={isProcessing || !selectedPackage}
                    variant="primary"
                    className={primaryBtn}
                  />
                  <Button
                    text={t('result.postSessionAi.skip', 'Skip')}
                    onClick={onSkip}
                    variant="secondary"
                    className={secondaryBtn}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </GlassPanel>

      <PinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSubmit={handlePinSubmit}
        title={t('payment.codeEntry.title', 'Enter Access Code')}
        subtitle={t('payment.codeEntry.subtitle', 'Enter 6-digit code to bypass payment')}
      />

      {showVoucherInput && (
        <OnScreenKeyboard
          onEnter={handleVoucherSubmit}
          placeholder={t('checkout.voucherCodePlaceholder', 'Type or paste your code')}
          error={voucherError}
          isLoading={isVoucherLoading}
          onInputChange={() => setVoucherError('')}
          onClose={() => {
            setShowVoucherInput(false)
            setVoucherError('')
          }}
        />
      )}
    </BackgroundContainer>
  )
}

export default PostSessionAiPaymentModal
